import { Box, Drawer, IconButton, Typography, Divider, Stack } from '@mui/material';
import React, { createContext, useContext, useEffect } from 'react';

import { colors } from '../theme/theme';

export const DrawerVisibilityContext = createContext<{
    setDrawerOpen: (open: boolean) => void;
} | null>(null);

interface CustomDrawerProps {
    open: boolean;
    onClose: () => void;
    anchor?: 'left' | 'right' | 'top' | 'bottom';
    width?: string | number;
    title?: string;
    showHeader?: boolean;
    children: React.ReactNode;
    footer?: React.ReactNode;
    sticky?: boolean;
    StickyHeight?: string | number;
    zIndex?: number;
}

export const CustomDrawer: React.FC<CustomDrawerProps> = ({
    open,
    onClose,
    anchor = 'right',
    width = 380,
    title,
    showHeader = true,
    children,
    footer,
    sticky = false,
    StickyHeight = 'calc(100dvh - 175px)',
    zIndex,
}) => {
    const drawerVisibility = useContext(DrawerVisibilityContext);

    useEffect(() => {
        if (!open) {
            return;
        }

        drawerVisibility?.setDrawerOpen(true);
        return () => {
            drawerVisibility?.setDrawerOpen(false);
        };
    }, [open, drawerVisibility]);

    return (
        <Drawer
            anchor={anchor}
            open={open}
            onClose={onClose}
            sx={zIndex ? { zIndex } : undefined}
            PaperProps={{
                role: 'dialog',
                'aria-modal': true,
                'aria-label': title || 'Dialog',
                sx: {
                    ...(zIndex ? { zIndex } : {}),
                    width: anchor === 'left' || anchor === 'right' ? { xs: '100%', sm: width } : '100%',
                    maxWidth: '100vw',
                    height: anchor === 'top' || anchor === 'bottom' ? 'auto' : '100%',
                    padding: '0px',
                    overflow: 'hidden',
                },
            }}
        >
            {/* HEADER (optional) */}
            {showHeader && (
                <Stack direction={'row'} p={2} justifyContent={'space-between'}>
                    <Typography variant="h5" component={'h1'}>
                        {title}
                    </Typography>

                    <IconButton onClick={onClose} sx={{ p: 0 }}>
                        <span className="icon-close" style={{ color: colors.fullBlack }} />
                    </IconButton>
                </Stack>
            )}

            {showHeader && <Divider sx={{ borderColor: colors.greyPreset[30] }} />}

            {/* CONTENT */}
            <Box overflow={'auto'} py={3} maxWidth={'100%'}>
                <Stack
                    px={3}
                    sx={{
                        ...(sticky && {
                            height: StickyHeight,
                            overflow: 'auto',
                            position: 'sticky',
                        }),
                    }}
                >
                    {children}
                </Stack>
                <Stack px={3}>{footer}</Stack>
            </Box>
        </Drawer>
    );
};
